import { NavLink, useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  Building2,
  ChevronRight,
  ClipboardList,
  FileText,
  FolderKanban,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  PhoneCall,
  Settings,
  ShoppingBag,
  Users,
  Wallet,
  Layers,
  Wrench,
  Tv,
  MapPin,
  ScanFace,
  CalendarClock,
  CalendarCheck,
  CalendarOff,
} from "lucide-react";
import { TePidIcon } from "../components/TePidLogo";
import { resolveMediaUrl } from "../utils/mediaUrl";
import { canRaiseServiceCall } from "../components/service/serviceDeskMeta";

const ICONS = {
  dashboard:     LayoutDashboard,
  users:         Users,
  approvals:     BadgeCheck,
  tasks:         ClipboardList,
  crm:           FolderKanban,
  serviceCalls:  PhoneCall,
  expenses:      Wallet,
  documents:     FileText,
  reports:       FileText,
  purchaseOrders: ShoppingBag,
  departments:   Layers,
  settings:      Settings,
  help:          HelpCircle,
  serviceDepartment: Wrench,
  tv:            Tv,
  liveMap:       MapPin,
  faceAttendance: ScanFace,
  attendance:    CalendarClock,
  myAttendance:  CalendarCheck,
  leave:         CalendarOff,
};

// Live Map is on hold until a proper map-tile provider replaces the
// now-blocked raw OpenStreetMap tile server (see MapView.js /
// PunchLocationMap.jsx) — flip this back on once that's wired up. Every
// role's "/live-map" entry below is left in place on purpose; this flag is
// the only thing hiding it, so re-enabling is a one-line change.
const LIVE_MAP_ENABLED = false;

const UTILITY_NAV = [
  { to: "/settings", label: "Settings",      icon: "settings" },
  { to: "/help",     label: "Help & Support", icon: "help" },
];

const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: "dashboard" },
  { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
  { to: "/live-map", label: "Live Map", icon: "liveMap" },
  { to: "/users", label: "Users", icon: "users" },
  { to: "/departments", label: "Departments", icon: "departments" },
  { to: "/approvals", label: "Approvals", icon: "approvals", badge: true },
  { to: "/tasks", label: "Tasks", icon: "tasks" },
  { to: "/crm", label: "CRM", icon: "crm" },
  { to: "/service-calls", label: "Service Calls", icon: "serviceCalls" },
  { to: "/expenses", label: "Expenses", icon: "expenses" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
  { to: "/documents", label: "Documents", icon: "documents" },
  { to: "/leave", label: "Leave Request", icon: "leave" },
  { to: "/work-reports", label: "Work Reports", icon: "reports" },
];

// Attendance / Face Attendance are super_admin-exclusive — admin does not
// get these links. Keep in sync with ATTENDANCE_ADMIN_ROLES / FACE_ATTENDANCE_ROLES
// in App.js and app/core/permissions.py on the backend.
const SUPER_ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: "dashboard" },
  { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
  { to: "/live-map", label: "Live Map", icon: "liveMap" },
  { to: "/face-attendance", label: "Face Attendance", icon: "faceAttendance" },
  { to: "/attendance", label: "Attendance", icon: "attendance" },
  { to: "/users", label: "Users", icon: "users" },
  { to: "/departments", label: "Departments", icon: "departments" },
  { to: "/approvals", label: "Approvals", icon: "approvals", badge: true },
  { to: "/tasks", label: "Tasks", icon: "tasks" },
  { to: "/crm", label: "CRM", icon: "crm" },
  { to: "/service-calls", label: "Service Calls", icon: "serviceCalls" },
  { to: "/expenses", label: "Expenses", icon: "expenses" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
  { to: "/documents", label: "Documents", icon: "documents" },
  { to: "/leave", label: "Leave Request", icon: "leave" },
  { to: "/work-reports", label: "Work Reports", icon: "reports" },
];

const NAV = {
  admin: ADMIN_NAV,
  super_admin: SUPER_ADMIN_NAV,
  supervisor: [
    { to: "/", label: "Dashboard", icon: "dashboard" },
    { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
    { to: "/live-map", label: "Live Map", icon: "liveMap" },
    { to: "/users", label: "Users", icon: "users" },
    { to: "/approvals", label: "Approvals", icon: "approvals", badge: true },
    { to: "/tasks", label: "Tasks", icon: "tasks" },
    { to: "/crm", label: "CRM", icon: "crm" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
    { to: "/service-calls", label: "Service Calls", icon: "serviceCalls" },
    { to: "/expenses", label: "Expenses", icon: "expenses" },
    { to: "/documents", label: "Documents", icon: "documents" },
    { to: "/leave", label: "Leave Request", icon: "leave" },
    { to: "/work-reports", label: "Work Reports", icon: "reports" },
  ],
  coordinator: [
    { to: "/", label: "Dashboard", icon: "dashboard" },
    { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
    { to: "/live-map", label: "Live Map", icon: "liveMap" },
    { to: "/approvals", label: "Approvals", icon: "approvals", badge: true },
    { to: "/tasks", label: "Tasks", icon: "tasks" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
    { to: "/service-calls", label: "Service Calls", icon: "serviceCalls" },
    { to: "/expenses", label: "My Expenses", icon: "expenses" },
    { to: "/documents", label: "Documents", icon: "documents" },
    { to: "/leave", label: "Leave Request", icon: "leave" },
    { to: "/work-reports", label: "Work Reports", icon: "reports" },
  ],
  finance: [
    { to: "/", label: "Dashboard", icon: "dashboard" },
    { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
    { to: "/users", label: "Users", icon: "users" },
    { to: "/tasks", label: "Tasks", icon: "tasks" },
    { to: "/approvals", label: "Approvals", icon: "approvals", badge: true },
    { to: "/expenses", label: "Expenses", icon: "expenses" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
    { to: "/documents", label: "Documents", icon: "documents" },
    { to: "/leave", label: "Leave Request", icon: "leave" },
    { to: "/work-reports", label: "Work Reports", icon: "reports" },
  ],
  employee: [
    { to: "/", label: "Dashboard", icon: "dashboard" },
    { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
    { to: "/tasks", label: "Tasks", icon: "tasks" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
    { to: "/expenses", label: "My Expenses", icon: "expenses" },
    { to: "/leave", label: "Leave Request", icon: "leave" },
    { to: "/work-reports", label: "Work Reports", icon: "reports" },
    { to: "/documents", label: "Documents", icon: "documents" },
  ],
  crm: [
    { to: "/", label: "Dashboard", icon: "dashboard" },
    { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
    { to: "/tasks", label: "Tasks", icon: "tasks" },
    { to: "/crm", label: "CRM", icon: "crm" },
    { to: "/expenses", label: "My Expenses", icon: "expenses" },
    { to: "/documents", label: "Documents", icon: "documents" },
    { to: "/leave", label: "Leave Request", icon: "leave" },
    { to: "/work-reports", label: "Work Reports", icon: "reports" },
  ],
  service_coordinator: [
    { to: "/", label: "Dashboard", icon: "dashboard" },
    { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
    { to: "/live-map", label: "Live Map", icon: "liveMap" },
    { to: "/approvals", label: "Approvals", icon: "approvals", badge: true },
    { to: "/tasks", label: "Tasks", icon: "tasks" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
    { to: "/service-calls", label: "Service Calls", icon: "serviceCalls" },
    { to: "/expenses", label: "My Expenses", icon: "expenses" },
    { to: "/documents", label: "Documents", icon: "documents" },
    { to: "/leave", label: "Leave Request", icon: "leave" },
    { to: "/work-reports", label: "Work Reports", icon: "reports" },
  ],
  finance_officer: [
    { to: "/", label: "Dashboard", icon: "dashboard" },
    { to: "/my-attendance", label: "My Attendance", icon: "myAttendance" },
    { to: "/tasks", label: "Tasks", icon: "tasks" },
    { to: "/approvals", label: "Approvals", icon: "approvals", badge: true },
    { to: "/expenses", label: "Expenses", icon: "expenses" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: "purchaseOrders" },
    { to: "/documents", label: "Documents", icon: "documents" },
    { to: "/leave", label: "Leave Request", icon: "leave" },
    { to: "/work-reports", label: "Work Reports", icon: "reports" },
  ],
  tv_monitor: [
    { to: "/tv-dashboard", label: "TV Monitor", icon: "tv" },
  ],
};

// Menu is grouped by business stage (lead -> order -> execution -> money), the
// way ERPs lay theirs out. Role arrays above only decide WHICH links a role
// gets; this decides where each one sits. A section with no visible links for
// the role is not rendered.
const NAV_SECTIONS = [
  { key: "overview",  label: "Overview",       paths: ["/", "/approvals", "/tv-dashboard"] },
  { key: "sales",     label: "Sales",          paths: ["/crm", "/purchase-orders"] },
  { key: "execution", label: "Execution",      paths: ["/tasks", "/service-calls", "/work-reports", "/live-map"] },
  { key: "finance",   label: "Finance",        paths: ["/expenses"] },
  { key: "people",    label: "People",         paths: ["/my-attendance", "/face-attendance", "/attendance", "/leave"] },
  { key: "admin",     label: "Administration", paths: ["/users", "/departments", "/documents"] },
];

function groupNavItems(items) {
  const placed = new Set();
  const groups = NAV_SECTIONS.map((section) => {
    const sectionItems = section.paths
      .map((path) => items.find((item) => item.to === path))
      .filter(Boolean);
    sectionItems.forEach((item) => placed.add(item.to));
    return { key: section.key, label: section.label, items: sectionItems };
  }).filter((group) => group.items.length > 0);

  // Any link not mapped above still shows up rather than silently vanishing.
  const rest = items.filter((item) => !placed.has(item.to));
  if (rest.length > 0) groups.push({ key: "other", label: "More", items: rest });
  return groups;
}

const ROLE_LABEL = {
  admin:               "Admin",
  super_admin:         "Super Admin",
  supervisor:          "HOD",
  coordinator:         "Co-Ordinator",
  service_coordinator: "Co-Ordinator",
  finance:             "Finance",
  finance_officer:     "Finance",
  employee:            "Employee",
  crm:                 "CRM",
  tv_monitor:          "TV Monitor",
};

const ROLE_COLOR = {
  admin: "bg-orange-500",
  supervisor: "bg-blue-500",
  finance: "bg-emerald-500",
  coordinator: "bg-violet-500",
  employee: "bg-slate-400",
  crm: "bg-cyan-500",
  super_admin: "bg-orange-500",
  tv_monitor: "bg-sky-500",
  service_coordinator: "bg-violet-500",
  finance_officer: "bg-emerald-500",
};

function SidebarContent({ role, items, isExpanded, onLinkClick, onToggle, onLogout, user, navigate }) {
  const avatarUrl = resolveMediaUrl(user.avatar_url);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-slate-950 text-slate-100">
      <button
        type="button"
        onClick={onToggle}
        title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        className="flex-shrink-0 border-b border-slate-800/90 px-4 py-5 text-left transition hover:bg-slate-900/70"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 border border-slate-800 shadow-lg shadow-black/20">
            <TePidIcon size={28} color="white" />
          </div>
          <div
            className="overflow-hidden"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? 170 : 0,
              transition: "opacity 0.2s ease, max-width 0.25s ease",
              whiteSpace: "nowrap",
            }}
          >
            <div className="text-sm font-semibold tracking-wide text-white">
              TePid
            </div>
            <div className="text-[11px] font-medium tracking-[0.18em] text-slate-400 uppercase">
              Industries Pvt Ltd
            </div>
          </div>
          {isExpanded && <ChevronRight className="ml-auto h-4 w-4 text-slate-500" />}
        </div>
      </button>

      <div className="flex-shrink-0 border-b border-slate-800/80 px-4 py-4">
        <div className={`flex items-center gap-2 ${!isExpanded ? "justify-center" : ""}`}>
          <span className={`h-2.5 w-2.5 rounded-full ${ROLE_COLOR[role] || "bg-slate-500"}`} />
          <span
            className="overflow-hidden text-xs font-medium uppercase tracking-[0.18em] text-slate-400"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? 170 : 0,
              transition: "opacity 0.2s ease, max-width 0.25s ease",
              whiteSpace: "nowrap",
            }}
          >
            {ROLE_LABEL[role] || role}
          </span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {groupNavItems(items).map((group, groupIndex) => (
          <div key={group.key} className={groupIndex === 0 ? "" : "mt-3"}>
            {isExpanded ? (
              <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                {group.label}
              </div>
            ) : (
              groupIndex > 0 && <div className="mx-3 mb-2 border-t border-slate-800/80" />
            )}
            <div className="space-y-1">
        {group.items.map((item) => {
          const Icon = ICONS[item.icon] || Building2;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              title={!isExpanded ? item.label : undefined}
              onClick={onLinkClick}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                  !isExpanded ? "justify-center" : ""
                } ${
                  isActive
                    ? "border border-slate-700 bg-slate-800/90 text-white shadow-sm"
                    : "text-slate-400 hover:bg-slate-900 hover:text-slate-100"
                }`
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span
                className="overflow-hidden font-medium"
                style={{
                  opacity: isExpanded ? 1 : 0,
                  maxWidth: isExpanded ? 180 : 0,
                  transition: "opacity 0.2s ease, max-width 0.25s ease",
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
              </span>
              {item.badge && isExpanded && (
                <span className="ml-auto rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-200">
                  Live
                </span>
              )}
            </NavLink>
          );
        })}
            </div>
          </div>
        ))}
      </nav>

      <div className="flex-shrink-0 border-t border-slate-800/60 px-3 py-2">
        {UTILITY_NAV.map((item) => {
          const Icon = ICONS[item.icon];
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onLinkClick}
              title={!isExpanded ? item.label : undefined}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                  !isExpanded ? "justify-center" : ""
                } ${
                  isActive
                    ? "border border-slate-700 bg-slate-800/90 text-white shadow-sm"
                    : "text-slate-400 hover:bg-slate-900 hover:text-slate-100"
                }`
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span
                className="overflow-hidden font-medium"
                style={{
                  opacity: isExpanded ? 1 : 0,
                  maxWidth: isExpanded ? 180 : 0,
                  transition: "opacity 0.2s ease, max-width 0.25s ease",
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>

      <div className="border-t border-slate-800/90 px-3 py-4 space-y-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => {
            navigate("/profile");
            onLinkClick?.();
          }}
          title="My Profile"
          className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 transition hover:bg-slate-900/80 ${
            !isExpanded ? "justify-center" : ""
          }`}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="avatar"
              className="h-9 w-9 flex-shrink-0 rounded-full border border-slate-700 object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-slate-800 text-sm font-semibold text-white">
              {(user.full_name || user.email || "U").charAt(0).toUpperCase()}
            </div>
          )}
          <div
            className="min-w-0 overflow-hidden text-left"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? 170 : 0,
              transition: "opacity 0.2s ease, max-width 0.25s ease",
              whiteSpace: "nowrap",
            }}
          >
            <div className="truncate text-xs font-semibold text-slate-200">
              {user.full_name || user.email || "User"}
            </div>
            <div className="truncate text-[11px] text-slate-500">
              {({admin:"ADMIN",super_admin:"ADMIN",supervisor:"HOD",coordinator:"CO-ORDINATOR",service_coordinator:"CO-ORDINATOR",finance:"FINANCE",finance_officer:"FINANCE",employee:"EMPLOYEE",crm:"CRM"})[user.role] || (user.role || "Member").toUpperCase()}
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={onLogout}
          title="Sign out"
          className={`flex w-full items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-300 transition hover:border-rose-500 hover:text-rose-300 ${
            isExpanded ? "" : "px-2"
          }`}
        >
          <LogOut className="h-4 w-4" />
          {isExpanded && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );
}

export default function Sidebar({
  role,
  onLogout,
  mobileOpen,
  onMobileClose,
  desktopExpanded,
  onDesktopToggle,
}) {
  const navigate = useNavigate();
  let user = {};
  try { user = JSON.parse(localStorage.getItem("user") || "{}"); } catch { user = {}; }
  const items = (NAV[role] || NAV.employee).filter((item) => {
    if (item.to === "/live-map") return LIVE_MAP_ENABLED;
    if (item.to === "/service-calls") return canRaiseServiceCall(role);
    return true;
  });
  // Everyone can raise and follow service calls, whatever their role's menu lists.
  if (canRaiseServiceCall(role) && !items.some((item) => item.to === "/service-calls")) {
    items.push({ to: "/service-calls", label: "Service Calls", icon: "serviceCalls" });
  }

  const sharedProps = { role, items, user, navigate, onLogout };

  return (
    <>
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-40 w-72 flex flex-col bg-slate-950 text-white shadow-2xl transition-transform duration-300 ease-in-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarContent
          {...sharedProps}
          isExpanded={true}
          onToggle={onMobileClose}
          onLinkClick={onMobileClose}
        />
      </aside>

      <aside
        className="hidden h-[100dvh] min-h-[100dvh] flex-shrink-0 self-stretch overflow-hidden bg-slate-950 text-white shadow-xl lg:sticky lg:top-0 lg:flex lg:flex-col"
        style={{
          width: desktopExpanded ? 256 : 76,
          minWidth: desktopExpanded ? 256 : 76,
          transition: "width 0.25s ease, min-width 0.25s ease",
        }}
      >
        <SidebarContent
          {...sharedProps}
          isExpanded={desktopExpanded}
          onToggle={onDesktopToggle}
          onLinkClick={null}
        />
      </aside>
    </>
  );
}
